// export type Parcel = {
//   ll_uuid: string
// }

import type { Geometry } from 'geojson'

export type ParcelFeatureCollection = {
  type: 'FeatureCollection'
  features: ParcelFeature[]
}

export type ParcelFeature = {
  type: 'Feature'
  id: number
  geometry: Geometry
  properties: ParcelProperties
}

export type ParcelProperties = {
  headline: string
  path: string
  fields: Fields
  ll_uuid: string
}

export type Fields = {
  ogc_fid: number
  geoid: string
  parcelnumb: string
  parcelnumb_no_formatting: string
  usecode: string
  zoning: string
  zoning_description: string
  zoning_type: string
  zoning_subtype: string
  zoning_code_link: string
  zoning_id: number
  numunits: number
  parvaltype: string
  improvval: number
  landval: number
  agval: number
  parval: number
  taxyear: string
  owner: string
  unmodified_owner: string
  mailadd: string
  mail_city: string
  mail_state2: string
  mail_zip: string
  original_mailing_address: string
  address: string
  saddno: string
  saddstr: string
  saddsttyp: string
  sunit?: string
  scity: string
  original_address: string
  city: string
  county: string
  state2: string
  szip: string
  szip5: string
  ll_address_count: number
  address_source: string
  neighborhood_code: string
  lat: string
  lon: string
  fema_flood_zone: string
  fema_flood_zone_subtype: string
  fema_flood_zone_raw: string
  fema_flood_zone_data_date: string
  fema_nri_risk_rating: string
  qoz: string
  census_tract: string
  census_block: string
  census_blockgroup: string
  census_zcta: string
  census_unified_school_district: string
  ll_last_refresh: string
  sqft?: number
  gisacre: number
  ll_gisacre: number
  ll_gissqft: number
  ll_bldg_footprint_sqft: number
  ll_bldg_count: number
  path: string
  ll_stable_id: string
  ll_uuid: string
  ll_updated_at: string
  precisely_id: string
  placekey: string
  dpv_status: string
  dpv_codes: string
  dpv_notes: string
  dpv_type: string
  cass_errorno: string
  rdi: string
  usps_vacancy: string
  usps_vacancy_date: string
  lbcs_activity: number
  lbcs_activity_desc: string
  lbcs_function: number
  lbcs_function_desc: string
  lbcs_structure: number
  lbcs_structure_desc: string
  lbcs_site: number
  lbcs_site_desc: string
  lbcs_ownership: number
  lbcs_ownership_desc: string
  housing_affordability_index: number
  population_density: number
  population_growth_past_5_years: number
  population_growth_next_5_years: number
  housing_growth_past_5_years: number
  housing_growth_next_5_years: number
  household_income_growth_next_5_years: number
  median_household_income: number
  transmission_line_distance: number
  roughness_rating: number
  highest_parcel_elevation: number
  lowest_parcel_elevation: number
  book: number
  page: number
  lot: number
  saledate: string
  saleprice: number
  sourceurl: string
  subdivision: string
  saddpref: string
}

const PARCEL_DATA_KEY_LABELS: { [key: string]: string } = {
  parcelnumb: 'Parcel ID',
  address: 'Address',
  address2: 'Address 2',
  sunit: 'Unit',
  scity: 'City',
  county: 'County',
  state2: 'State Abbr',
  szip5: 'Zip Code',
  subdivision: 'Subdivision',
  lat: 'Latitude',
  lon: 'Longitude',
  // usecode: 'Land Use Code',
  zoning: 'Zoning',
  zoning_description: 'Zoning Desc',
  zoning_type: 'Zoning Type',
  zoning_subtype: 'Zoning Subtype',
  zoning_code_link: 'Zoning Code Link',
  qoz: 'Opportunity Zone',
  sqft: 'Square Feet',
  gisacre: 'Acres',
  ll_gisacre: 'Acres Calc',
  ll_bldg_footprint_sqft: 'Building Size Calc',
  ll_bldg_count: '# of Buildings',
  ll_address_count: '# of Addresses',
  numunits: '# of Units',
  taxyear: 'Tax Year',
  legaldesc: 'Legal Description',
  fema_flood_zone: 'FEMA Flood Zone',
  fema_flood_zone_subtype: 'FEMA Flood Zone Subtype',
  fema_flood_zone_data_date: 'FEMA Flood Zone Data Date',
  fema_nri_risk_rating: 'FEMA NRI Risk Rating',
  lowest_parcel_elevation: 'Lowest Elevation',
  highest_parcel_elevation: 'Highest Elevation',
  roughness_rating: 'Roughness Rating',
  transmission_line_distance: 'Transmission Line Distance',

  landval: 'Land Value',
  agval: 'Agricultural Value',
  improvval: 'Improvement Value',
  parval: 'Total Value',
  parvaltype: 'Parcel Value Type',
  saledate: 'Sale Date',
  saleprice: 'Sale Price',

  owner: 'Owner',
  mailadd: 'Owner Address',
  mail_city: 'Owner City',
  mail_state2: 'Owner State',
  mail_zip: 'Owner Zip Code',

  census_unified_school_district: 'Census Unified School District',
  rdi: 'Residential Delivery Indicator',
  usps_vacancy: 'USPS Vacancy',
  usps_vacancy_date: 'USPS Vacancy Date',
  housing_affordability_index: 'Housing Affordability Index',
  population_density: 'Population Density',
  population_growth_past_5_years: 'Population Growth (Past 5 Years)',
  population_growth_next_5_years: 'Population Growth (Next 5 Years)',
  housing_growth_past_5_years: 'Housing Growth (Past 5 Years)',
  housing_growth_next_5_years: 'Housing Growth (Next 5 Years)',
  household_income_growth_next_5_years:
    'Household Income Growth (Next 5 Years)',
  median_household_income: 'Median Household Income',

  sourceurl: 'Source URL',
  ll_updated_at: 'Updated Date',
}

const orderedKeys = Object.keys(PARCEL_DATA_KEY_LABELS)
const ignoredKeys = [
  'geoid',
  'fema_flood_zone_raw',
  'usecode',
  'parcelnumb_no_formatting',
  'll_gissqft',

  'original_address',
  'original_mailing_address',
  'saddno',
  'saddstr',
  'saddsttyp',
  'city',
  'szip',
  'szip5',
  'path',
  'zoning_id',
  'cass_errorno',
  'census_tract',
  'census_block',
  'census_blockgroup',
  'census_zcta',
  'unmodified_owner',
  'll_last_refresh',
  'll_stable_id',
  'll_uuid',
  'precisely_id',
  'placekey',
  'dpv_status',
  'dpv_codes',
  'dpv_notes',
  'dpv_type',
  'lbcs_activity',
  'lbcs_activity_desc',
  'lbcs_function',
  'lbcs_function_desc',
  'lbcs_structure',
  'lbcs_structure_desc',
  'lbcs_site',
  'lbcs_site_desc',
  'lbcs_ownership',
  'lbcs_ownership_desc',
  'address_source',
  'cass_errorno',
  'neighborhood_code',
  'ogc_fid',
  'plss_range',
  'plss_section',
  'plss_township',
  'saddstsuf',
  'cdl_date',
  'cdl_majority_category',
  'cdl_majority_percent',
  'cdl_raw',
  'qoz_tract',
  'book',
  'page',
  'lot',
  'saddpref',
  'll_stack_uuid',
]

export function getParcelLabelValues(parcelData: ParcelProperties) {
  const matchingKeys: string[] = []
  const nonMatchingKeys: string[] = []

  Object.keys(parcelData.fields).forEach((key) => {
    if (key in PARCEL_DATA_KEY_LABELS) {
      matchingKeys.push(key)
    } else if (!ignoredKeys.includes(key)) {
      nonMatchingKeys.push(key)
    }
  })

  matchingKeys.sort((a, b) => orderedKeys.indexOf(a) - orderedKeys.indexOf(b))
  nonMatchingKeys.sort()

  return matchingKeys.concat(nonMatchingKeys).map((key) => ({
    key,
    label: PARCEL_DATA_KEY_LABELS[key] || key, //.replace(/_/g, ' '), //camelCaseToWords(key.replace(/_/g, ' ')),
    value: parcelData.fields[key as keyof Fields],
  }))
}
